import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery } from "react-query";
import axios from "axios";
import {
  RiFileTextLine,
  RiFolder3Line,
  RiFileList3Line,
} from "react-icons/ri";
import { TCustomer } from "types/customer";
import { getPrimaryCustomerHeaderContacts } from "utils/customerContactInfo";
import {
  InvoiceStatementRow,
  InvoiceFilterType,
  StatementGroup,
  OverallTotals,
  InvoiceStatusOption,
  TypeFilterOption,
} from "../types";
import { useStatementDateFilter } from "./useStatementDateFilter";

interface UseStatementInvoicesParams {
  customerId: string;
  customerInfo?: TCustomer;
  tr: (key: string) => string;
  dateFilterHook: ReturnType<typeof useStatementDateFilter>;
}

export function useStatementInvoices({
  customerId,
  customerInfo,
  tr,
  dateFilterHook,
}: UseStatementInvoicesParams) {
  const [onlyOutstanding, setOnlyOutstanding] = useState<boolean>(true);
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState<InvoiceFilterType>("all");
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState<boolean>(false);
  const typeDropdownRef = useRef<HTMLDivElement>(null);

  const [invoiceStatusFilters, setInvoiceStatusFilters] = useState<string[]>([]);
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState<boolean>(false);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  const [searchTerm, setSearchTerm] = useState<string>("");

  // Global click outside & escape listener for dropdowns
  const { isDateDropdownOpen, setIsDateDropdownOpen, dateDropdownRef } = dateFilterHook;

  // Filter options for invoice types
  const typeFilterOptions: TypeFilterOption[] = useMemo(
    () => [
      {
        value: "all",
        label: tr("All Invoices"),
        description: tr("Show all billable invoices"),
        icon: RiFileTextLine,
        iconColor: "text-slate-600",
        bgColor: "bg-slate-100",
      },
      {
        value: "request",
        label: tr("Request Invoices"),
        description: tr("Invoices tied to requests"),
        icon: RiFileList3Line,
        iconColor: "text-emerald-600",
        bgColor: "bg-emerald-50",
      },
      {
        value: "project",
        label: tr("Project Invoices"),
        description: tr("Invoices tied to projects"),
        icon: RiFolder3Line,
        iconColor: "text-blue-600",
        bgColor: "bg-blue-50",
      },
    ],
    [tr]
  );

  const activeTypeOption = useMemo(
    () => typeFilterOptions.find((opt) => opt.value === invoiceTypeFilter) || typeFilterOptions[0],
    [typeFilterOptions, invoiceTypeFilter]
  );

  // Queries
  const statementQuery = useQuery<{ rows: InvoiceStatementRow[] }>(
    ["customer-statement-invoices", customerId],
    async () => {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v2/company/customers/${customerId}/invoices`,
        {
          params: { limit: 1000, page: 1, includeStatementFields: true },
        }
      );
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const customerDetailsQuery = useQuery(
    ["customer-details-statement", customerId],
    async () => {
      if (customerInfo) return customerInfo;
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v2/company/customers/${customerId}`
      );
      return res.data?.info;
    },
    {
      enabled: !customerInfo,
    }
  );

  const companySettingsQuery = useQuery(
    ["company-settings-statement"],
    async () => {
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/companySettings`
      );
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const customerData = customerInfo || customerDetailsQuery.data;
  const allInvoices = useMemo(() => statementQuery.data?.rows || [], [statementQuery.data?.rows]);

  // Resolved Customer Contact Info
  const { displayEmail: customerEmail, displayPhone: customerPhone } = useMemo(() => {
    if (customerData?.primary_contact_info?.length) {
      const favoriteEmail = customerData.primary_contact_info.find(
        (e: any) => e.contact_type === "email" && e.is_primary && String(e.contact_value || "").trim()
      );
      const favoritePhone = customerData.primary_contact_info.find(
        (e: any) => e.contact_type === "phone" && e.is_primary && String(e.contact_value || "").trim()
      );
      return {
        displayEmail: favoriteEmail
          ? String(favoriteEmail.contact_value).trim()
          : (customerData?.email || "").trim(),
        displayPhone: favoritePhone
          ? String(favoritePhone.contact_value).trim()
          : (customerData?.phone || "").trim(),
      };
    }
    return getPrimaryCustomerHeaderContacts(
      customerData?.extended_contacts,
      customerData?.email,
      customerData?.phone
    );
  }, [customerData]);

  // Status lists and counts
  const availableStatuses = useMemo(() => {
    const map = new Map<string, InvoiceStatusOption>();

    const defaultStatuses = [
      { label: "Open", color: "#3b82f6" },
      { label: "Unpaid", color: "#ef4444" },
      { label: "Paid", color: "#10b981" },
      { label: "Partially Paid", color: "#f59e0b" },
    ];

    const defaultColorMap = new Map(
      defaultStatuses.map((status) => [
        status.label.toLowerCase(),
        status.color,
      ])
    );

    allInvoices.forEach((inv) => {
      if (!inv.status?.label) return;

      const label = inv.status.label.trim();
      const key = label.toLowerCase();

      if (!map.has(key)) {
        map.set(key, {
          label,
          id: inv.status.id,
          color: inv.status.color || defaultColorMap.get(key),
        });
      }
    });

    // Add default statuses if they don't already exist
    // and ensure existing ones have a color.
    defaultStatuses.forEach((status) => {
      const key = status.label.toLowerCase();
      const existing = map.get(key);

      map.set(key, {
        ...status,
        ...existing,
        color: existing?.color || status.color,
      });
    });

    return Array.from(map.values());
  }, [allInvoices]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    allInvoices.forEach((inv) => {
      if (inv.status?.label) {
        const key = inv.status.label.trim().toLowerCase();
        counts[key] = (counts[key] || 0) + 1;
      }
    });
    return counts;
  }, [allInvoices]);

  // Filtered invoices
  const filteredInvoices = useMemo(() => {
    return allInvoices.filter((inv) => {
      // 1. Outstanding filter
      if (onlyOutstanding) {
        const invoiceStatus = inv.status?.label?.trim().toLowerCase();
        if (invoiceStatusFilters.some((status) => status.toLowerCase() === "paid")) {
          // If explicitly filtering by Paid status, do not exclude
        } else if (
          inv.amount_due <= 0 ||
          invoiceStatus === "paid" ||
          invoiceStatus === "open"
        ) {
          return false;
        }
      }

      // 2. Type filter
      if (invoiceTypeFilter === "project") {
        const isProject = Boolean(inv.fk_project_id || inv.project_name);
        if (!isProject) return false;
      } else if (invoiceTypeFilter === "request") {
        const isRequest = Boolean(inv.fk_request_id && !inv.fk_project_id && !inv.project_name);
        if (!isRequest) return false;
      }

      // 3. Status filter
      if (invoiceStatusFilters.length > 0) {
        const invoiceStatus = (inv.status?.label || "").trim().toLowerCase();
        if (!invoiceStatusFilters.some((status) => status.toLowerCase() === invoiceStatus)) {
          return false;
        }
      }

      // 4. Date filter
      if (!dateFilterHook.matchesDateFilter(inv.creation_date)) {
        return false;
      }

      // 5. Search filter
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase().trim().replace(/^#/, "");
        const invId = (inv.id || "").toString().toLowerCase().replace(/^#/, "");
        const reqId = (inv.fk_request_id || "").toString().toLowerCase().replace(/^#/, "");
        const reqDesc = (inv.request_description || "").toLowerCase();
        const projName = (inv.project_name || "").toLowerCase();
        const propAddr = (inv.property_address || "").toLowerCase();
        return (
          invId.includes(q) ||
          reqId.includes(q) ||
          reqDesc.includes(q) ||
          projName.includes(q) ||
          propAddr.includes(q)
        );
      }
      return true;
    });
  }, [
    allInvoices,
    onlyOutstanding,
    invoiceTypeFilter,
    invoiceStatusFilters,
    dateFilterHook,
    searchTerm,
  ]);

  // Grouped invoices
  const groupedInvoices = useMemo(() => {
    const groups: StatementGroup[] = [];
    const groupMap = new Map<string, InvoiceStatementRow[]>();

    filteredInvoices.forEach((inv) => {
      let groupKey = "general";
      if (inv.fk_project_id || inv.project_name) {
        groupKey = `project_${inv.fk_project_id || inv.project_name}`;
      } else if (inv.fk_request_id) {
        groupKey = `request_${inv.fk_request_id}`;
      }

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, []);
      }
      groupMap.get(groupKey)!.push(inv);
    });

    groupMap.forEach((invoices, key) => {
      const first = invoices[0];
      let title = tr("General / Standalone Invoices");
      let subtitle = "";
      let type: "request" | "project" | "general" = "general";

      if (key.startsWith("project_")) {
        type = "project";
        title = first.project_name
          ? `${tr("Project")}: ${first.project_name}`
          : `${tr("Project")} #${first.fk_project_id}`;
      } else if (key.startsWith("request_")) {
        type = "request";
        title = `${tr("Request")} #${first.fk_request_id}`;
        if (first.request_description) {
          subtitle = first.request_description;
        }
      }

      const totalInvoiced = invoices.reduce((acc, i) => acc + (i.actual_total || 0), 0);
      const totalPaid = invoices.reduce((acc, i) => acc + (i.paid_total || 0), 0);
      const totalDue = invoices.reduce((acc, i) => acc + (i.amount_due || 0), 0);

      groups.push({
        key,
        title,
        subtitle,
        type,
        invoices,
        totalInvoiced,
        totalPaid,
        totalDue,
      });
    });

    return groups;
  }, [filteredInvoices, tr]);

  // Overall financial totals
  const overallTotals: OverallTotals = useMemo(() => {
    const totalInvoiced = filteredInvoices.reduce((acc, i) => acc + (i.actual_total || 0), 0);
    const totalPaid = filteredInvoices.reduce((acc, i) => acc + (i.paid_total || 0), 0);
    const totalDue = filteredInvoices.reduce((acc, i) => acc + (i.amount_due || 0), 0);

    return {
      totalInvoiced,
      totalPaid,
      totalDue,
      count: filteredInvoices.length,
    };
  }, [filteredInvoices]);

  // Close dropdowns on outside click or Escape
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(target)) {
        setIsTypeDropdownOpen(false);
      }
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(target)) {
        setIsDateDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(target)) {
        setIsStatusDropdownOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsTypeDropdownOpen(false);
        setIsDateDropdownOpen(false);
        setIsStatusDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [dateDropdownRef, setIsDateDropdownOpen]);

  const handleSelectStatus = (statusLabel: string) => {
    setInvoiceStatusFilters((current) => {
      const isSelected = current.some(
        (status) => status.toLowerCase() === statusLabel.toLowerCase()
      );
      return isSelected
        ? current.filter((status) => status.toLowerCase() !== statusLabel.toLowerCase())
        : [...current, statusLabel];
    });
    if (["paid", "open"].includes(statusLabel.toLowerCase()) && onlyOutstanding) {
      setOnlyOutstanding(false);
    }
  };

  return {
    customerData,
    customerEmail,
    customerPhone,
    allInvoices,
    statementQuery,
    companySettingsQuery,
    onlyOutstanding,
    setOnlyOutstanding,
    invoiceTypeFilter,
    setInvoiceTypeFilter,
    isTypeDropdownOpen,
    setIsTypeDropdownOpen,
    typeDropdownRef,
    typeFilterOptions,
    activeTypeOption,
    invoiceStatusFilters,
    setInvoiceStatusFilters,
    isStatusDropdownOpen,
    setIsStatusDropdownOpen,
    statusDropdownRef,
    availableStatuses,
    statusCounts,
    handleSelectStatus,
    searchTerm,
    setSearchTerm,
    filteredInvoices,
    groupedInvoices,
    overallTotals,
  };
}
